/**
 * GPU particle shader.
 *
 * Particles are *write-once*: everything a particle will ever do is baked into its
 * instance attributes at spawn time and the vertex shader evaluates its trajectory
 * analytically from elapsed time. That means the CPU touches a particle exactly once
 * — no per-frame integration, no per-frame buffer upload for anything already in
 * flight. A thousand-particle explosion costs one partial buffer update and nothing
 * afterwards.
 *
 * The motion model is linear drag plus constant gravity, which has a closed form:
 *
 *   v(t) = (v0 - vT)·e^(-kt) + vT          vT = g/k  (terminal velocity)
 *   x(t) = x0 + vT·t + (v0 - vT)(1 - e^(-kt))/k
 *
 * Drag matters: dust that keeps its launch velocity looks like sparks, and sparks
 * that don't decelerate look like fireworks. One exponential buys both.
 */

export const particleVert = /* glsl */ `
attribute vec3 iOrigin;   // spawn position
attribute vec3 iVel;      // spawn velocity
attribute vec4 iLife;     // birth, lifetime, drag k, gravity scale
attribute vec4 iSize;     // size at birth, size at death, roll, roll speed
attribute vec4 iCol0;     // rgb at birth, peak alpha
attribute vec4 iCol1;     // rgb at death, atlas cell index
attribute vec4 iOpt;      // shade mode, velocity stretch, fade-in fraction, fade-out power

uniform float uTime;
uniform vec2  uAtlasGrid;   // cols, rows
uniform vec2  uNearFade;    // start, end distance for the lens fade
uniform float uPxScale;     // world units per pixel at one metre of depth
uniform float uMinPixels;   // minimum on-screen width for a stretched sprite
uniform float uSoftDist;    // base depth-fade distance; also declared in the fragment stage

varying vec2  vUv;
varying vec4  vColor;
varying float vViewZ;
varying float vRoll;
varying float vShade;
varying vec2  vFlip;
varying float vSoftDist;

void main() {
  float t = uTime - iLife.x;
  float life = iLife.y;

  // Dead or unborn: throw the vertex past the far plane so it is clipped before
  // rasterisation. Cheaper than any CPU-side compaction.
  if (t < 0.0 || t >= life) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float age = t / life;

  /* ---- analytic trajectory ---- */
  float k = max(iLife.z, 0.05);
  vec3  g = vec3(0.0, -9.81 * iLife.w, 0.0);
  vec3  vT = g / k;
  float e = exp(-k * t);
  vec3  wpos = iOrigin + vT * t + (iVel - vT) * (1.0 - e) / k;
  vec3  vel  = (iVel - vT) * e + vT;

  vec4 mv = modelViewMatrix * vec4(wpos, 1.0);

  /* ---- size: ease-out so puffs bloom fast then settle ---- */
  float ease = 1.0 - pow(1.0 - age, 2.2);
  float sz = mix(iSize.x, iSize.y, ease);
  float roll = iSize.z + iSize.w * t;

  vec2 corner = position.xy;
  vec2 off;

  if (iOpt.y > 0.0) {
    // Velocity-aligned billboard. Sparks and debris trails must smear along the
    // direction they travel — an unstretched spark reads as a floating dot.
    vec3 velView = (viewMatrix * vec4(vel, 0.0)).xyz;
    vec2 d = velView.xy;
    float dl = length(d);
    vec2 dir = dl > 1e-4 ? d / dl : vec2(0.0, 1.0);
    vec2 perp = vec2(-dir.y, dir.x);
    float len = sz * (1.0 + iOpt.y * length(vel));
    // A heavily stretched sprite gets very thin, and a quad narrower than a pixel
    // rasterises into a dotted line instead of a streak. Hold it to a minimum
    // on-screen width; the alpha ramp keeps it from looking fat up close.
    float w = max(sz, uMinPixels * max(-mv.z, 0.01) * uPxScale);
    off = dir * (corner.y * len) + perp * (corner.x * w);
  } else {
    float c = cos(roll), s = sin(roll);
    off = vec2(corner.x * c - corner.y * s, corner.x * s + corner.y * c) * sz;
  }

  mv.xy += off;
  vViewZ = -mv.z;
  gl_Position = projectionMatrix * mv;

  /* ---- atlas cell ----
     Mirror the sprite per particle. Eight overlapping copies of one puff texture
     read as eight copies of one puff texture — the eye finds the repeated motif
     immediately. Two random flips give four variants for free, derived from the
     roll angle so no extra attribute is needed. */
  float idx = floor(iCol1.w + 0.5);
  float col = mod(idx, uAtlasGrid.x);
  float row = floor(idx / uAtlasGrid.x);
  vec2 flip = vec2(
    fract(iSize.z * 0.1591549) > 0.5 ? -1.0 : 1.0,
    fract(iSize.z * 0.0731143) > 0.5 ? -1.0 : 1.0
  );
  vec2 quv = vec2(flip.x > 0.0 ? uv.x : 1.0 - uv.x, flip.y > 0.0 ? uv.y : 1.0 - uv.y);
  vUv = (quv + vec2(col, row)) / uAtlasGrid;
  vFlip = flip;

  /* ---- colour ramp + fades ---- */
  float fadeIn  = smoothstep(0.0, max(iOpt.z, 1e-4), age);
  float fadeOut = pow(1.0 - age, max(iOpt.w, 0.05));
  float lens = smoothstep(uNearFade.x, uNearFade.y, vViewZ);

  vColor = vec4(mix(iCol0.rgb, iCol1.rgb, age), iCol0.a * fadeIn * fadeOut * lens);
  vRoll = roll;
  vShade = iOpt.x;

  // The depth over which a sprite fades into geometry scales with the sprite: a
  // distance that hides the seam on a 30 cm dust puff leaves a 3 m ball of
  // explosion smoke sliced into visible horizontal slabs where it crosses the
  // ground. It has to be capped, though — let it grow with the sprite unbounded
  // and a large cloud sitting near a wall fades itself out of existence.
  //
  // The *lower* bound has to stay near zero now that the additive pass fades too.
  // A quarter-metre floor is right for a dust puff and catastrophic for a spark: a
  // spark is born a centimetre off the wall it just hit, so a 0.26 m ramp erases
  // the entire shower at the exact moment it should be brightest. Scaling from the
  // sprite's own size with only a hair of floor gives a 6 mm spark a 3 mm fade
  // (i.e. none) and a 3 m smoke ball the full 0.8 m, which is what each needs.
  vSoftDist = clamp(sz * 0.5, max(uSoftDist * 0.12, 0.02), 0.8);
}
`;

export const particleFrag = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3  uSunDirView;   // sun direction in view space
uniform vec3  uSunColor;
uniform vec3  uAmbient;

#ifdef SOFT
uniform sampler2D uDepth;
uniform vec2  uInvRes;
uniform float uCamNear;
uniform float uCamFar;
uniform float uSoftDist;
#endif

varying vec2  vUv;
varying vec4  vColor;
varying float vViewZ;
varying float vRoll;
varying float vShade;
varying vec2  vFlip;
varying float vSoftDist;

void main() {
  vec4 tex = texture2D(uAtlas, vUv);
  float a = tex.a * vColor.a;
  if (a < 0.0035) discard;

  vec3 rgb;

  if (vShade > 0.5) {
    /* Normal-mapped puff. The atlas stores a billow normal in RG and an internal
       density in B, so a smoke sprite is lit like a lump of geometry instead of
       being a flat grey disc. This — plus wrap lighting, because smoke forward-
       scatters hard — is what separates volume from blob. */
    // Mirroring the sprite mirrors its normal too, or the lighting stops agreeing
    // with the shape it is lighting.
    vec2 nxy = (tex.rg * 2.0 - 1.0) * vFlip;
    float c = cos(vRoll), s = sin(vRoll);
    vec2 rn = vec2(nxy.x * c - nxy.y * s, nxy.x * s + nxy.y * c);
    vec3 n = vec3(rn, sqrt(max(0.0, 1.0 - dot(rn, rn))));

    float wrap = dot(n, uSunDirView) * 0.5 + 0.5;
    float lit = pow(wrap, 1.6);

    // Thin edges transmit: the rim of a puff glows when backlit.
    float thin = 1.0 - tex.a;
    float trans = thin * thin * max(0.0, -uSunDirView.z * 0.5 + 0.5) * 0.55;

    // Dense cores self-shadow. This is doing more work than it looks: a puff read
    // against a bright wall has almost no contrast with its background, so the
    // contrast that makes it legible has to come from *within* the sprite. Push it
    // too far, though, and the opaque centre goes darker than the translucent rim
    // and the sprite reads as a smoke ring — a 2:1 range is the useful limit.
    float occl = mix(1.08, 0.52, tex.b);

    rgb = vColor.rgb * (uAmbient * occl + uSunColor * lit * occl + uSunColor * trans);
  } else {
    // Unlit sprites take their shape from alpha alone and never touch the atlas
    // RGB. Two of the eight cells store a packed normal there rather than a
    // colour, and multiplying by one produces rainbow fringing — this way any cell
    // is safe to use as a shape mask for an emissive particle.
    rgb = vColor.rgb;
  }

#ifdef SOFT
  // Soft particles: fade where the sprite intersects solid geometry. Hard-edged
  // smoke slicing through a wall is the single most obvious "this is a billboard"
  // tell there is.
  vec2 suv = gl_FragCoord.xy * uInvRes;
  float d = texture2D(uDepth, suv).x;
  float sceneZ = uCamNear * uCamFar / (uCamFar - d * (uCamFar - uCamNear));
  // smoothstep rather than a linear clamp: a linear ramp still shows a visible
  // crease where the sprite meets a surface at a grazing angle.
  a *= smoothstep(0.0, vSoftDist, sceneZ - vViewZ);
#endif

  gl_FragColor = vec4(rgb * a, a);
}
`;
